import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Save, Loader2, Eye, EyeOff, Home, Calendar, Users, Award, 
  Flag, ChevronDown, ChevronRight, Globe, Layout, Layers, 
  MessageSquare, Edit2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pageIcons = {
  home: Home,
  program: Calendar,
  speakers: Users,
  sponsorship: Award,
  golf: Flag,
  exhibit: Layout
};

const AdminVisibility = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibility, setVisibility] = useState(null);
  const [expandedPages, setExpandedPages] = useState({});
  const [editingMessage, setEditingMessage] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchVisibility();
  }, [navigate]);

  const fetchVisibility = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/content/visibility`);
      if (response.ok) {
        const data = await response.json();
        setVisibility(data);
        // Expand all pages by default
        const expanded = {};
        Object.keys(data.pages || {}).forEach(page => {
          expanded[page] = true;
        });
        setExpandedPages(expanded);
      }
    } catch (error) {
      console.error('Failed to fetch visibility settings:', error);
      toast.error('Failed to load visibility settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/content/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(visibility)
      });
      
      if (response.ok) {
        toast.success('Visibility settings saved successfully');
      } else {
        toast.error('Failed to save visibility settings');
      }
    } catch (error) {
      console.error('Failed to save visibility settings:', error);
      toast.error('Failed to save visibility settings');
    } finally {
      setSaving(false);
    }
  };

  const togglePageVisibility = (pageKey) => {
    setVisibility(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageKey]: {
          ...prev.pages[pageKey],
          visible: !prev.pages[pageKey].visible
        }
      }
    }));
  };

  const toggleSectionVisibility = (pageKey, sectionKey) => {
    setVisibility(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [pageKey]: {
          ...prev.sections[pageKey],
          [sectionKey]: {
            ...prev.sections[pageKey][sectionKey],
            visible: !prev.sections[pageKey][sectionKey].visible
          }
        }
      }
    }));
  };

  const toggleNavigationOption = (optionKey) => {
    setVisibility(prev => ({
      ...prev,
      navigation: {
        ...prev.navigation,
        [optionKey]: !prev.navigation[optionKey]
      }
    }));
  };

  const updateHiddenMessage = (pageKey, field, value) => {
    setVisibility(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageKey]: {
          ...prev.pages[pageKey],
          hidden_message: {
            ...prev.pages[pageKey]?.hidden_message,
            [field]: value
          }
        }
      }
    }));
  };

  const toggleExpanded = (pageKey) => {
    setExpandedPages(prev => ({
      ...prev,
      [pageKey]: !prev[pageKey]
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Eye className="w-6 h-6 text-[#00457f]" />
              Page & Section Visibility
            </h1>
            <p className="text-gray-600 mt-1">
              Control which pages and sections are visible. Hidden pages show a custom message.
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#00457f] hover:bg-[#003366]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <EyeOff className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Hidden Pages</p>
                  <p className="text-sm text-blue-700">
                    When a page is hidden, visitors see a custom message. Edit the message below each page toggle.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Layers className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Hidden Sections</p>
                  <p className="text-sm text-amber-700">
                    Hidden sections are simply not rendered on the page. No message is shown.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation Options */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#00457f]" />
              Navigation Options
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <Label htmlFor="show_register" className="font-medium">
                  Show "Register" Button
                </Label>
                <Switch
                  id="show_register"
                  checked={visibility?.navigation?.show_register_button ?? true}
                  onCheckedChange={() => toggleNavigationOption('show_register_button')}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <Label htmlFor="show_attend" className="font-medium">
                  Show "Attend" Link
                </Label>
                <Switch
                  id="show_attend"
                  checked={visibility?.navigation?.show_attend_link ?? true}
                  onCheckedChange={() => toggleNavigationOption('show_attend_link')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pages and Sections */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#00457f]" />
              Pages & Sections
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {visibility?.pages && Object.entries(visibility.pages).map(([pageKey, pageData]) => {
              const PageIcon = pageIcons[pageKey] || Layout;
              const isExpanded = expandedPages[pageKey];
              const sections = visibility.sections?.[pageKey] || {};
              const visibleSectionsCount = Object.values(sections).filter(s => s.visible).length;
              const totalSectionsCount = Object.keys(sections).length;
              const isEditingThis = editingMessage === pageKey;
              
              return (
                <div 
                  key={pageKey} 
                  className={`border rounded-xl overflow-hidden transition-all ${
                    pageData.visible 
                      ? 'border-gray-200 bg-white' 
                      : 'border-red-300 bg-red-50/50'
                  }`}
                >
                  {/* Page Header */}
                  <div className={`flex items-center justify-between p-4 ${
                    pageData.visible ? 'bg-gray-50' : 'bg-red-100/50'
                  }`}>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleExpanded(pageKey)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      <PageIcon className={`w-5 h-5 ${pageData.visible ? 'text-[#00457f]' : 'text-red-500'}`} />
                      <div>
                        <span className="font-semibold text-gray-900">
                          {pageData.label}
                        </span>
                        {totalSectionsCount > 0 && (
                          <span className="ml-2 text-sm text-gray-500">
                            ({visibleSectionsCount}/{totalSectionsCount} sections)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {!pageData.visible && (
                        <button
                          onClick={() => setEditingMessage(isEditingThis ? null : pageKey)}
                          className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          <MessageSquare className="w-4 h-4" />
                          {isEditingThis ? 'Close' : 'Edit Message'}
                        </button>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${pageData.visible ? 'text-green-600' : 'text-red-600'}`}>
                          {pageData.visible ? 'Visible' : 'Hidden'}
                        </span>
                        <Switch
                          checked={pageData.visible}
                          onCheckedChange={() => togglePageVisibility(pageKey)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Hidden Page Message Editor */}
                  {!pageData.visible && isEditingThis && (
                    <div className="p-4 border-t border-red-200 bg-red-50/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Edit2 className="w-4 h-4 text-red-600" />
                        <span className="font-medium text-red-800">Hidden Page Message</span>
                      </div>
                      <div className="grid gap-3">
                        <div>
                          <Label className="text-sm text-gray-700">Header</Label>
                          <Input
                            value={pageData.hidden_message?.header || ''}
                            onChange={(e) => updateHiddenMessage(pageKey, 'header', e.target.value)}
                            placeholder="e.g., Coming Soon"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-gray-700">Subtitle</Label>
                          <Input
                            value={pageData.hidden_message?.subtitle || ''}
                            onChange={(e) => updateHiddenMessage(pageKey, 'subtitle', e.target.value)}
                            placeholder="e.g., This page is under construction"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-gray-700">Body Text</Label>
                          <Textarea
                            value={pageData.hidden_message?.body || ''}
                            onChange={(e) => updateHiddenMessage(pageKey, 'body', e.target.value)}
                            placeholder="Additional details for visitors..."
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Sections */}
                  {isExpanded && totalSectionsCount > 0 && (
                    <div className="p-4 border-t bg-white">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                        Page Sections (hidden sections are not displayed)
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(sections).map(([sectionKey, sectionData]) => (
                          <div
                            key={sectionKey}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              sectionData.visible 
                                ? 'bg-white border-gray-200' 
                                : 'bg-gray-100 border-gray-300'
                            } ${!pageData.visible ? 'opacity-50' : ''}`}
                          >
                            <span className={`text-sm ${sectionData.visible ? 'text-gray-700' : 'text-gray-500'}`}>
                              {sectionData.label}
                            </span>
                            <Switch
                              checked={sectionData.visible}
                              onCheckedChange={() => toggleSectionVisibility(pageKey, sectionKey)}
                              disabled={!pageData.visible}
                            />
                          </div>
                        ))}
                      </div>
                      {!pageData.visible && (
                        <p className="text-sm text-gray-500 mt-3 italic">
                          Section toggles are disabled because this page is hidden
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminVisibility;
